import React, { Suspense } from 'react';
import { base44 } from '@/api/base44Client';
import usePageTitle from '@/hooks/usePageTitle';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import CSHeroSlider from '@/components/home/CSHeroSlider';
import CSValueBar from '@/components/home/CSValueBar';
import CSCategoryBanners from '@/components/home/CSCategoryBanners';
import CSProductCardSkeleton from '@/components/products/CSProductCardSkeleton';

// CSProductCard (et ses icônes lucide) est chargé en chunk séparé, hors du
// bundle initial, pour réduire le parse/exécution JS au 1er rendu (TBT desktop).
const CSProductCard = React.lazy(() => import('@/components/products/CSProductCard'));

function SkeletonGrid({ count, cols }) {
  return (
    <div className={`grid ${cols} gap-4`}>
      {Array.from({ length: count }).map((_, i) => <CSProductCardSkeleton key={i} />)}
    </div>
  );
}

export default function CSHome() {
  usePageTitle('', 'Circuit-Slot, spécialiste des circuits de slot Scalextric & Carrera pour la famille : coffrets, voitures, rails et accessoires, analogique et digital. Livraison 24/72h.');
  const { data: featured = [], isLoading: featLoading } = useQuery({
    queryKey: ['cs-products-featured'],
    queryFn: () => base44.entities.Product.filter({ featured: true }, '-created_date', 6),
  });

  const { data: nouveautes = [], isLoading: nouvLoading } = useQuery({
    queryKey: ['cs-products-nouveautes'],
    queryFn: () => base44.entities.Product.filter({ badge: 'NOUVEAU' }, '-created_date', 4),
  });

  const needRecent = featured.length === 0 && !featLoading;
  const { data: recent = [], isLoading: recentLoading } = useQuery({
    queryKey: ['cs-products-recent'],
    queryFn: () => base44.entities.Product.list('-created_date', 6),
    enabled: needRecent,
  });

  const popularLoading = featLoading || (needRecent && recentLoading);
  const popular = featured.length > 0 ? featured : recent;
  const showNouveautes = nouvLoading || nouveautes.length > 0;

  return (
    <div className="bg-white">
      <CSHeroSlider />
      <CSValueBar />

      {/* Produits Populaires */}
      <section className="max-w-screen-xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-bold text-2xl text-gray-900">PRODUITS POPULAIRES</h2>
          <Link to="/shop" className="flex items-center gap-1 text-sm text-cs-blue font-semibold hover:gap-2 transition-all">
            VOIR TOUS LES PRODUITS <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {popularLoading ? (
          <SkeletonGrid count={6} cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" />
        ) : (
          <Suspense fallback={<SkeletonGrid count={6} cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" />}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {popular.map(p => (
                <CSProductCard key={p.id} product={p} />
              ))}
            </div>
          </Suspense>
        )}
      </section>

      {/* Category Banners */}
      <CSCategoryBanners />

      {/* Nouveautés */}
      {showNouveautes && (
        <section className="max-w-screen-xl mx-auto px-4 pb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display font-bold text-2xl text-gray-900">NOUVEAUTÉS</h2>
            <Link to="/shop?badge=NOUVEAU" className="flex items-center gap-1 text-sm text-cs-blue font-semibold hover:gap-2 transition-all">
              VOIR TOUT <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {nouvLoading ? (
            <SkeletonGrid count={4} cols="grid-cols-2 md:grid-cols-4" />
          ) : (
            <Suspense fallback={<SkeletonGrid count={4} cols="grid-cols-2 md:grid-cols-4" />}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {nouveautes.map(p => <CSProductCard key={p.id} product={p} />)}
              </div>
            </Suspense>
          )}
        </section>
      )}

    </div>
  );
}